import { useContext, useMemo, useRef, useState, useEffect } from "react";
import { Button, Flex, Spin, Tag, Tooltip, Typography } from "antd";
import {
    CodeOutlined,
    ExclamationCircleOutlined,
    PlayCircleOutlined,
    RightOutlined,
    RobotOutlined,
    ToolOutlined,
} from "@ant-design/icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import styled from "styled-components";
import LayoutWrapper from "../../../../components/utility/layoutWrapper";
import CreateTaskHeader from "../../../ScrumBoard/Task/TaskCreateOrUpdate/CreateTaskHeader/CreateTaskHeader";
import { AppContext } from "../../../../context/ContextProvider";
import PodTerminal from "../../../../components/Terminal/PodTerminal";

// Same display map as ControlExpandInfo + ClusterDashboard.
const FRAMEWORK_DISPLAY = {
    "mitre":       { label: "MITRE",   tooltip: "MITRE ATT&CK for Containers",            color: "purple" },
    "cis-v1.10.0": { label: "CIS",     tooltip: "CIS Kubernetes Benchmark v1.10.0",       color: "blue" },
    "cis-eks":     { label: "CIS-EKS", tooltip: "CIS Amazon EKS Benchmark",                color: "blue" },
    "cis-aks":     { label: "CIS-AKS", tooltip: "CIS Azure Kubernetes Benchmark",          color: "blue" },
    "nsa":         { label: "NSA",     tooltip: "NSA-CISA Kubernetes Hardening Guidance",  color: "geekblue" },
    "armobest":    { label: "Armo",    tooltip: "ArmoBest (kubescape opinionated baseline)", color: "green" },
    "devopsbest":  { label: "DevOps",  tooltip: "Kubescape DevOps Best Practices",         color: "cyan" },
    "allcontrols": { label: "All",     tooltip: "Kubescape AllControls bundle",            color: "default" },
    "soc2":        { label: "SOC 2",   tooltip: "SOC 2 mapping",                            color: "gold" },
    "pci":         { label: "PCI",     tooltip: "PCI DSS",                                  color: "orange" },
    "hipaa":       { label: "HIPAA",   tooltip: "HIPAA",                                    color: "magenta" },
};

const SEVERITY_COLOR = { Critical: "red", High: "volcano", Medium: "orange", Low: "gold" };

// Some kubescape control classes have empty fix `value` by design — the
// control flags the field as offending but can't suggest a literal value
// (e.g. C-0012 detects credentials in env vars; the fix is "move it to a
// Secret", not "set it to '' "). Map those to a useful one-liner.
const EMPTY_VALUE_HINTS = {
    "applications-credentials-in-configuration-files":
        "Move this value to a Kubernetes Secret and reference it via env.valueFrom.secretKeyRef.",
    "exposed-sensitive-interfaces":
        "Remove the public exposure (Service type, hostPort) or protect with NetworkPolicy + auth.",
    "list-kubernetes-secrets":
        "Narrow this RBAC binding so it can't list secrets cluster-wide.",
};

// Languages we treat as runnable in the kubectl terminal. Markdown fences
// like ```bash, ```sh, ```kubectl, and bare ``` all count; ```yaml is a
// manifest snippet (copyable but not directly runnable).
const RUNNABLE_LANGS = new Set(["bash", "sh", "shell", "kubectl", ""]);

const Title = styled("h2")`
    font-size: 14px;
    font-family: Poppins;
    color: #292929;
    margin-top: 0;
`;
const Subtitle = styled("p")`
    font-size: 12px;
    font-family: Nunito Sans;
    color: #5e6366;
    margin: 0;
`;
const PathRow = styled("div")`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 8px 0;
    border-bottom: 1px dashed #e8e8e8;
    &:last-child { border-bottom: none; }
`;
const PathArrow = styled("span")`
    color: #6486EE;
    font-size: 10px;
    margin-top: 6px;
`;
const PathKey = styled("code")`
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
    font-size: 12px;
    color: #1f1f1f;
    background: #f5f7fb;
    padding: 2px 6px;
    border-radius: 4px;
    word-break: break-all;
`;
const PathValue = styled("code")`
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
    font-size: 12px;
    color: #0a8043;
    background: #e6f7ec;
    padding: 2px 6px;
    border-radius: 4px;
    margin-left: 4px;
`;
const HintText = styled("span")`
    font-family: Nunito Sans;
    font-size: 12px;
    color: #ff8a05;
    font-style: italic;
`;

// Extract fenced code blocks from a markdown string. Returns
// [{ language, code }] in document order. Handles ```bash, ```yaml,
// ```kubectl, and bare ``` blocks. Used to drive the "Open in Terminal"
// action list under the AI response.
const CODE_FENCE_RE = /```([a-zA-Z0-9_+-]*)\s*\n?([\s\S]*?)```/g;
function extractCodeBlocks(markdown) {
    if (!markdown) return [];
    const out = [];
    let m;
    CODE_FENCE_RE.lastIndex = 0;
    while ((m = CODE_FENCE_RE.exec(markdown)) !== null) {
        const lang = (m[1] || "").trim().toLowerCase();
        const code = (m[2] || "").trim();
        if (!code) continue;
        out.push({ language: lang, code });
    }
    return out;
}

export default function FailedControlsDrawer({ data, clusterUniqueId, autoRunAnalysis }) {
    const { setOpenRightDrawer } = useContext(AppContext);
    const handelCancel = () => {
        setOpenRightDrawer({ drawerType: null, drawerProps: { data: null } });
    };

    // ControlExpandInfo passes the control metadata in `__control` and the
    // per-resource row at the top level (kind, name, namespace, failedRules).
    const control = data?.__control || null;
    const resource = data || {};
    const failedRules = Array.isArray(resource.failedRules) ? resource.failedRules : [];

    const severityColor = control?.severity ? SEVERITY_COLOR[control.severity] : null;

    // === Path rendering (same as before) ===
    const flatPaths = useMemo(() => {
        const out = [];
        failedRules.forEach((rule) => {
            const paths = Array.isArray(rule?.paths) ? rule.paths : [];
            paths.forEach((p) => {
                out.push({
                    ruleName: rule?.name || "",
                    path: p?.fixPath?.path || "—",
                    value: p?.fixPath?.value,
                    isEmptyValue: !p?.fixPath?.value || p?.fixPath?.value === "-" || p?.fixPath?.value === "",
                });
            });
        });
        return out;
    }, [failedRules]);

    const dedupedPaths = useMemo(() => {
        const seen = new Set();
        return flatPaths.filter((p) => {
            const k = `${p.path}|${p.value || ""}`;
            if (seen.has(k)) return false;
            seen.add(k);
            return true;
        });
    }, [flatPaths]);

    const emptyValueHint = (ruleName) => EMPTY_VALUE_HINTS[ruleName] ||
        "Manual review needed — kubescape can't suggest a literal replacement value for this rule class.";

    // === AI Analysis state ===
    const [analysisState, setAnalysisState] = useState("idle"); // idle | streaming | complete | error
    const [analysisContent, setAnalysisContent] = useState("");
    const [analysisError, setAnalysisError] = useState("");
    const abortRef = useRef(null);
    // === Kubectl terminal state ===
    const [terminalVisible, setTerminalVisible] = useState(false);
    const [terminalNamespace, setTerminalNamespace] = useState(resource.namespace || "default");

    // Cancel any in-flight stream on unmount / drawer close.
    useEffect(() => () => abortRef.current?.abort(), []);

    const codeBlocks = useMemo(() => extractCodeBlocks(analysisContent), [analysisContent]);

    const handleRunAnalysis = async () => {
        if (analysisState === "streaming") return;
        setAnalysisState("streaming");
        setAnalysisContent("");
        setAnalysisError("");

        abortRef.current?.abort();
        abortRef.current = new AbortController();

        // Build a focused prompt with all the context the AI needs to
        // generate runnable verification + remediation commands.
        const lines = [];
        if (control?.id) lines.push(`Control: ${control.id} — ${control.name || ""}`.trim());
        if (control?.severity) lines.push(`Severity: ${control.severity}`);
        if (Array.isArray(control?.frameworks) && control.frameworks.length) {
            lines.push(`Frameworks: ${control.frameworks.join(", ")}`);
        }
        if (control?.description) lines.push(`What it checks: ${control.description}`);
        if (control?.remediation) lines.push(`Recommended fix: ${control.remediation}`);
        if (resource?.kind || resource?.name) {
            lines.push(`Affected resource: ${resource.kind || "?"} / ${resource.name || "?"} (namespace: ${resource.namespace || "—"})`);
        }
        if (dedupedPaths.length) {
            lines.push("Offending field paths:");
            dedupedPaths.forEach((p) => {
                lines.push(`  - ${p.path}` + (p.isEmptyValue ? "" : ` (suggested value: ${p.value})`));
            });
        }
        const contextBlock = lines.join("\n");

        const userPrompt = [
            "Analyse this kubescape failed control for the given Kubernetes resource and answer:",
            "1. In ONE sentence, why this is a security concern.",
            "2. Provide one or more `kubectl` commands inside ```bash``` code blocks that I can run to VERIFY the issue exists on the resource (e.g. inspect the relevant fields).",
            "3. Provide the remediation as either a `kubectl patch`/`apply` command inside ```bash``` or a YAML snippet inside ```yaml```.",
            "Keep prose under 5 lines total. Every command MUST be in a code block.",
        ].join("\n");

        try {
            // KubeGateway exposes the streaming chat at /api/chat/stream
            // (see chat.py:55). The schema is ChatMessage(message, ...),
            // there's no separate `system_message` field — fold the system
            // instructions into the user `message`. Stream chunks come as
            //   data: {"type":"content","message":"..."}\n\n
            //   data: {"type":"thread_id","thread_id":"..."}\n\n
            //   data: {"type":"end"}\n\n
            //   data: {"type":"error","message":"..."}\n\n
            const apiUrl = `${process.env.REACT_APP_KUBEGATEWAY}/api/chat/stream`;
            const systemInstruction = [
                "You are a Kubernetes security expert specialising in Kubescape findings.",
                "Given a failed control + affected resource, produce concise, accurate kubectl",
                "verification commands and a remediation. Always wrap every command in a ```bash```",
                "or ```yaml``` code fence. Do not invent CRDs or APIs that don't exist.",
            ].join(" ");
            const resp = await fetch(apiUrl, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("isAuth")}`,
                    "Content-Type": "application/json",
                },
                signal: abortRef.current.signal,
                body: JSON.stringify({
                    message: `${systemInstruction}\n\n${contextBlock}\n\n${userPrompt}`,
                    cluster_unique_id: clusterUniqueId,
                }),
            });
            if (!resp.body) throw new Error("Streaming not supported by this endpoint.");
            if (!resp.ok) {
                throw new Error(`AI endpoint returned ${resp.status} ${resp.statusText}`);
            }

            const reader = resp.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            let accumulated = "";
            let sawError = false;
            let stop = false;

            while (!stop) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split("\n");
                buffer = lines.pop();
                for (const raw of lines) {
                    const t = raw.trim();
                    if (!t.startsWith("data:")) continue;
                    let payload;
                    try {
                        payload = JSON.parse(t.replace(/^data:\s*/, ""));
                    } catch {
                        continue;
                    }
                    // Local KubeGateway tags every chunk with `type`. Branch
                    // on it explicitly so error/end frames don't get
                    // appended to the response as if they were text.
                    const ptype = payload.type;
                    if (ptype === "error") {
                        setAnalysisError(payload.message || payload.error || "AI request failed.");
                        sawError = true;
                        stop = true;
                        try { reader.cancel(); } catch (_) {}
                        break;
                    }
                    if (ptype === "end") {
                        stop = true;
                        break;
                    }
                    if (ptype === "thread_id") {
                        // Persistence metadata — not needed for kubescape flow.
                        continue;
                    }
                    // ptype === "content" (or legacy un-typed) — accumulate.
                    const chunk = payload.message || payload.textResponse || payload.content || "";
                    if (chunk) {
                        accumulated += chunk;
                        setAnalysisContent(accumulated);
                    }
                }
            }
            setAnalysisState(sawError ? "error" : "complete");
        } catch (err) {
            if (err?.name === "AbortError") return;
            setAnalysisError(err?.message || String(err));
            setAnalysisState("error");
        }
    };

    // Auto-trigger analysis when the drawer is opened via the "Run AI Analysis" table column button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { if (autoRunAnalysis) handleRunAnalysis(); }, []);

    // Build a paste-able banner of every runnable code block the AI suggested.
    // Mirrors the SRE-Agent IssueRow.terminalBanner pattern — ANSI escapes for
    // a colored header, then verbatim commands the user can copy and paste at
    // the shell prompt below. YAML blocks are intentionally omitted (they
    // belong in `kubectl apply -f -`, not at the shell prompt directly).
    const terminalBanner = useMemo(() => {
        const runnable = codeBlocks.filter((b) => RUNNABLE_LANGS.has(b.language));
        if (runnable.length === 0) return "";
        const lines = [
            "\x1b[1;36m=== Suggested commands from AI analysis ===\x1b[0m",
            `\x1b[2;37m(Control ${control?.id || ""} on ${resource?.kind || "?"}/${resource?.name || "?"})\x1b[0m`,
            "",
        ];
        runnable.forEach((blk, i) => {
            lines.push(`\x1b[1;33m[${i + 1}]\x1b[0m`);
            blk.code.split("\n").forEach((ln) => lines.push(`    ${ln}`));
            lines.push("");
        });
        lines.push("\x1b[2;37m(Copy a command above and paste it at the prompt to run it)\x1b[0m");
        return lines.join("\n");
    }, [codeBlocks, control?.id, resource?.kind, resource?.name]);

    const hasRunnableCommands = terminalBanner.length > 0;

    const handleOpenInTerminal = () => {
        if (!clusterUniqueId || !hasRunnableCommands) return;
        setTerminalNamespace(resource.namespace || "default");
        setTerminalVisible(true);
    };

    return (
        <LayoutWrapper className="clusterInfo" style={{ padding: 0 }}>
            <CreateTaskHeader buttonContent={"Failed Controls"} onCancel={handelCancel} />

            <div style={{ marginTop: 90, padding: "0 20px 100px", width: "100%" }}>
                {/* === Control header === */}
                {control && (
                    <div style={{ marginBottom: 16 }}>
                        <Flex align="center" wrap="wrap" gap={8} style={{ marginBottom: 8 }}>
                            <Typography.Text style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 16, color: "#1f1f1f" }}>
                                {control.id ? `${control.id} · ` : ""}{control.name || ""}
                            </Typography.Text>
                            {control.severity && severityColor && (
                                <Tag color={severityColor} style={{ fontWeight: 600 }}>{control.severity}</Tag>
                            )}
                            {Array.isArray(control.frameworks) && control.frameworks.map((fw, i) => {
                                const meta = FRAMEWORK_DISPLAY[(fw || "").toLowerCase()] || { label: fw, tooltip: fw, color: "default" };
                                return (
                                    <Tooltip key={i} title={meta.tooltip}>
                                        <Tag color={meta.color} style={{ fontSize: 11 }}>{meta.label}</Tag>
                                    </Tooltip>
                                );
                            })}
                        </Flex>

                        {(control.description || control.remediation) && (
                            <div style={{
                                background: "#fafbff",
                                border: "1px solid #e6eaf6",
                                borderRadius: 8,
                                padding: "12px 14px",
                            }}>
                                {control.description && (
                                    <div style={{ marginBottom: control.remediation ? 10 : 0 }}>
                                        <Flex align="center" gap={6} style={{ marginBottom: 4 }}>
                                            <ExclamationCircleOutlined style={{ color: "#ff8a05" }} />
                                            <Typography.Text strong style={{ fontFamily: "Poppins", fontSize: 13, color: "#1f1f1f" }}>
                                                What this checks
                                            </Typography.Text>
                                        </Flex>
                                        <Typography.Text style={{ fontFamily: "Nunito Sans", fontSize: 13, color: "#4a4a4a", lineHeight: 1.55 }}>
                                            {control.description}
                                        </Typography.Text>
                                    </div>
                                )}
                                {control.remediation && (
                                    <div>
                                        <Flex align="center" gap={6} style={{ marginBottom: 4 }}>
                                            <ToolOutlined style={{ color: "#52c41a" }} />
                                            <Typography.Text strong style={{ fontFamily: "Poppins", fontSize: 13, color: "#1f1f1f" }}>
                                                How to fix
                                            </Typography.Text>
                                        </Flex>
                                        <Typography.Text style={{ fontFamily: "Nunito Sans", fontSize: 13, color: "#4a4a4a", lineHeight: 1.55 }}>
                                            {control.remediation}
                                        </Typography.Text>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* === Affected resource (single resource per drawer) === */}
                <div style={{
                    border: "1px solid #e8e8e8",
                    borderRadius: 8,
                    padding: "14px 16px",
                    marginBottom: 16,
                }}>
                    <Flex align="center" gap={6} wrap="wrap" style={{ marginBottom: 6 }}>
                        <Typography.Text style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 13, color: "#1f1f1f" }}>
                            Affected resource
                        </Typography.Text>
                    </Flex>
                    <Flex align="center" gap={6} wrap="wrap" style={{ marginBottom: 12 }}>
                        {resource.kind && <Tag color="blue">{resource.kind}</Tag>}
                        <Typography.Text style={{ fontFamily: "Nunito Sans", fontWeight: 600, color: "#292929" }}>
                            {resource.name || "—"}
                        </Typography.Text>
                        {resource.namespace && (
                            <Typography.Text style={{ fontFamily: "Nunito Sans", fontSize: 12, color: "#5e6366" }}>
                                · namespace {resource.namespace}
                            </Typography.Text>
                        )}
                    </Flex>

                    {dedupedPaths.length === 0 ? (
                        <Subtitle>No specific paths reported for this resource.</Subtitle>
                    ) : (
                        <>
                            <Title>Paths to fix ({dedupedPaths.length})</Title>
                            {dedupedPaths.map((p, i) => (
                                <PathRow key={i}>
                                    <PathArrow><RightOutlined /></PathArrow>
                                    <div style={{ flex: 1 }}>
                                        <PathKey>{p.path}</PathKey>
                                        {p.isEmptyValue ? (
                                            <div style={{ marginTop: 4 }}>
                                                <Tooltip title={emptyValueHint(p.ruleName)}>
                                                    <HintText>↳ {emptyValueHint(p.ruleName)}</HintText>
                                                </Tooltip>
                                            </div>
                                        ) : (
                                            <span style={{ marginLeft: 4 }}>
                                                <span style={{ color: "#9b9b9b", fontSize: 12 }}>set to</span>
                                                <PathValue>{String(p.value)}</PathValue>
                                            </span>
                                        )}
                                    </div>
                                </PathRow>
                            ))}
                        </>
                    )}
                </div>

                {/* === AI Analysis === */}
                <div style={{
                    border: "1px solid #e8e8e8",
                    borderRadius: 8,
                    padding: "14px 16px",
                }}>
                    <Flex align="center" justify="space-between" wrap="wrap" gap={8} style={{ marginBottom: analysisState === "idle" ? 0 : 12 }}>
                        <Flex align="center" gap={8}>
                            <RobotOutlined style={{ color: "#6486EE", fontSize: 18 }} />
                            <Typography.Text style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 14, color: "#1f1f1f" }}>
                                AI Analysis
                            </Typography.Text>
                            {analysisState === "streaming" && <Spin size="small" />}
                        </Flex>
                        <Flex gap={8} wrap="wrap">
                            {/* Single "Run in terminal" button — mirrors the SRE-Agent
                                IssueRow CTA. Only shown once analysis is complete AND
                                the AI emitted at least one runnable command. Opens the
                                kubectl PodTerminal at the bottom of the screen with
                                every suggested command in the xterm scrollback. */}
                            {hasRunnableCommands && analysisState === "complete" && clusterUniqueId && (
                                <Tooltip title="Open a kubectl terminal pre-loaded with these commands as reference">
                                    <Button
                                        size="middle"
                                        icon={<CodeOutlined />}
                                        onClick={handleOpenInTerminal}
                                        style={{ borderColor: "#4D99EF", color: "#4D99EF" }}
                                    >
                                        Run in terminal
                                    </Button>
                                </Tooltip>
                            )}
                            <Button
                                type="primary"
                                icon={<PlayCircleOutlined />}
                                onClick={handleRunAnalysis}
                                loading={analysisState === "streaming"}
                                disabled={analysisState === "streaming"}
                            >
                                {analysisState === "idle" || analysisState === "error" ? "Run AI Analysis" : analysisState === "streaming" ? "Analysing…" : "Re-run analysis"}
                            </Button>
                        </Flex>
                    </Flex>

                    {analysisState === "idle" && (
                        <Typography.Text style={{ fontFamily: "Nunito Sans", fontSize: 12, color: "#9b9b9b" }}>
                            Get an AI explanation of this finding plus exact <code>kubectl</code> commands you can run to verify and fix it — opens in the cluster's kubectl terminal in one click.
                        </Typography.Text>
                    )}

                    {analysisError && (
                        <Typography.Text type="danger" style={{ fontSize: 12, fontFamily: "Nunito Sans" }}>
                            {analysisError}
                        </Typography.Text>
                    )}

                    {analysisContent && (
                        <div style={{
                            background: "#fbfbfd",
                            border: "1px solid #eee",
                            borderRadius: 6,
                            padding: "10px 12px",
                            marginTop: 8,
                            fontFamily: "Nunito Sans",
                            fontSize: 13,
                            lineHeight: 1.6,
                            color: "#292929",
                        }}>
                            <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                    code({ inline, className, children, ...props }) {
                                        if (inline) return <code style={{ background: "#f0f2f5", padding: "1px 5px", borderRadius: 3, fontSize: 12 }} {...props}>{children}</code>;
                                        return (
                                            <pre style={{
                                                background: "#1f1f1f",
                                                color: "#e6e6e6",
                                                padding: "10px 12px",
                                                borderRadius: 6,
                                                overflowX: "auto",
                                                fontSize: 12,
                                                fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                                                margin: "8px 0",
                                            }}>
                                                <code {...props}>{children}</code>
                                            </pre>
                                        );
                                    },
                                }}
                            >
                                {analysisContent}
                            </ReactMarkdown>
                        </div>
                    )}

                </div>
            </div>

            {/* Kubectl terminal — fixed-position modal owned by PodTerminal.
                Mounted lazily only after the user clicks "Run in terminal" so
                the xterm + websocket aren't created on every drawer open.
                Same pattern as the SRE-Agent IssueRow.js terminal mount. */}
            {terminalVisible && clusterUniqueId && (
                <PodTerminal
                    clusterUniqueId={clusterUniqueId}
                    pod={{ namespace: terminalNamespace }}
                    visible={terminalVisible}
                    onClose={() => setTerminalVisible(false)}
                    terminalType="kubectl"
                    initialBanner={terminalBanner}
                    autoConnect
                />
            )}
        </LayoutWrapper>
    );
}
